abstract class AbstractPinFactory {
  /** Original width of the pin without the shadow */
  originalWidth: number;
  /** Original height of the pin without the shadow */
  originalHeight: number;

  /** Additional width for the drop shadow */
  shadowWidth: number;
  /** Additional height for the drop shadow */
  shadowHeight: number;

  /** Desired width of the pin as displayed on the map */
  desiredScaledWidth: number;

  /** X Offset for centering the icon within the pin */
  iconOffsetX: number;
  /** Y Offset for centering the icon within the pin */
  iconOffsetY: number;

  /** Shadow SVG filter */
  shadow: string;

  constructor({
    originalWidth,
    originalHeight,
    shadowWidth,
    shadowHeight,
    desiredScaledWidth,
    iconOffsetX,
    iconOffsetY,
    shadow,
  }: {
    originalWidth: number;
    originalHeight: number;
    shadowWidth: number;
    shadowHeight: number;
    desiredScaledWidth: number;
    iconOffsetX: number;
    iconOffsetY: number;
    shadow: string;
  }) {
    this.originalWidth = originalWidth;
    this.originalHeight = originalHeight;
    this.shadowWidth = shadowWidth;
    this.shadowHeight = shadowHeight;
    this.desiredScaledWidth = desiredScaledWidth;
    this.iconOffsetX = iconOffsetX;
    this.iconOffsetY = iconOffsetY;
    this.shadow = shadow;
  }

  /** Total width of the pin including the shadow */
  get width() {
    return this.originalWidth + this.shadowWidth;
  }
  /** Total height of the pin including the shadow */
  get height() {
    return this.originalHeight + this.shadowHeight;
  }

  /** ViewBox adjusted to center the pin and not clip the shadow  */
  get viewBox() {
    return `-${this.shadowWidth / 2} 0 ${this.width} ${this.height}`;
  }

  get ratioHeightToWidth() {
    return this.height / this.width;
  }

  /** Scaled width of the pin including the shadow */
  get scaledWidth() {
    return this.desiredScaledWidth * (this.width / this.originalWidth);
  }
  /** Scaled height of just the pin without the shadow */
  get scaledOriginalHeight() {
    return this.scaledWidth * this.ratioHeightToWidth;
  }
  /** Scaled height of the pin including the shadow */
  get scaledHeight() {
    return this.scaledOriginalHeight * (this.height / this.originalHeight);
  }

  /** X Offset of pin point  */
  get anchorX() {
    return this.scaledWidth / 2;
  }
  /** Y Offset of pin point  */
  get anchorY() {
    return this.scaledOriginalHeight;
  }

  /**
   * Get the paths for the pin
   * @param color - The color of the pin
   * @returns The paths for the pin
   */
  abstract getPaths(color: string): string;

  createSvg(paths: string) {
    return `<svg width="${this.width}" height="${this.height}" viewBox="${this.viewBox}" fill="none" xmlns="http://www.w3.org/2000/svg">
   ${this.shadow}
   ${paths}
   </svg>`;
  }

  createPin(color: string) {
    const paths = this.getPaths(color);
    return {
      svg: this.createSvg(paths),
      width: this.scaledWidth,
      height: this.scaledHeight,
      anchorX: this.anchorX,
      anchorY: this.anchorY,
      iconOffsetX: this.iconOffsetX,
      iconOffsetY: this.iconOffsetY,
    };
  }
}

export { AbstractPinFactory };
